import { action, makeObservable, runInAction } from 'mobx';
import { UserStore } from './store';
import { readUser, readUserLevel, readUserScoreRecords, readPermissions, readUserMedal, readUsersThreads, createFollow, followDelete, userLogout, readFollowList, readRecommendUserList, searchUsersList, readUsersPosts, readUsersLikes, readUsersFavorites, readNotifications, unreadNotifications, getChatMessageList, getChatList, submitNotificationsRead, submitChatMessageSend, getChatRoomFind} from '@server';
import { clearAuthCookies, getCookie } from '../../utils/cookie';
import { getLogoutUrl } from '../../utils/logout';
const JUMP_URL_LABEL = '__jump_url';

export class UserAction extends UserStore {
    constructor(props) {
        super(props);
        makeObservable(this);
        this.loginHydrationPromise = null;
        this.loginStateVersion = 0;
    }
    isSessionStorage() {
        return typeof window !== 'undefined' && window.sessionStorage;
    }

    // 获取当前的url
    getCurrentUrl() {
        let url = window.location.href;
        return url;
    }
    /**
     * 检查是否已登录，未登录则跳转到 vivo oauth 登录
     * @param {string} platform 可选参数，平台类型 ('h5'|'pc')，不传时默认为 'h5'
     * @param {string} redirectUrl 可选参数，登录成功后需要跳转的地址；不传时使用当前地址
     * @param {boolean} force 接口确认登录失效时，忽略本地缓存并重新登录
     * @returns {boolean} 已登录返回 true，未登录返回 false 并跳转
     */
    checkLogin = (platform = 'h5', redirectUrl = '', force = false) => {
        const API_BASE_URL = process.env.REACT_APP_API_BASE || '';
        let OAUTH_URL = `${API_BASE_URL}/api/v3/users/vivo/oauth?platform=${platform}`;
        // 优先从 cookie 中读取 user_code
        const cookieUserCode = getCookie('user_code');
        // console.log('[checkLogin] cookie user_code:', cookieUserCode, 'OAUTH_URL:', OAUTH_URL);

        // 若 cookie 中存在 user_code，则认为已登录
        if (!force && cookieUserCode) {
            // console.log('[checkLogin] cookie 中存在 user_code，认为已登录');
            runInAction(() => {
                this.isLoggedIn = true;
            });
            return true;
        }

        // 如果 cookie 没有 user_code，则再检查内存状态（兼容单页应用场景）
        // console.log('[checkLogin] 检查内存状态:', { isLoggedIn: this.isLoggedIn, hasUserInfo: !!this.userInfo, hasToken: !!this.token });
        if (!force && (this.isLoggedIn || this.userInfo || this.token)) {
            // console.log('[checkLogin] 内存状态有登录信息，认为已登录');
            return true;
        }
        if (force) {
            runInAction(() => {
                this.isLoggedIn = false;
                this.userInfo = null;
                this.token = null;
            });
        }
        // 都没有则跳转到 OAuth 登录
        // console.log('[checkLogin] 未登录，准备跳转');
        if (typeof window !== 'undefined') {
            // 🔧 关键修复：不要保存需要登录的页面URL
            // 只保存非敏感页面（比如首页、列表页），防止无限循环
            const currentUrl = this.getCurrentUrl();
            const loginRedirectUrl = redirectUrl || currentUrl;
            const isProtectedRoute = loginRedirectUrl.includes('/setPost')
                || loginRedirectUrl.includes('/setFeedbackPost')
                || loginRedirectUrl.includes('/publish')
                || loginRedirectUrl.includes('/draft');
            
            // 显式传入的目标页优先级最高；旧调用方仍保持原有的保护路由逻辑
            if (redirectUrl || !isProtectedRoute) {
                this.setUrl(loginRedirectUrl);
            }
            // 其他情况下不保存，登录后跳转到首页
            // 获取页面路径上的inviteCode参数
            const urlParams = new URLSearchParams(window.location.search);
            const inviteCode = urlParams.get('inviteCode');
            
            // 如果存在inviteCode，拼接到链接后面
            if (inviteCode) {
                OAUTH_URL += `&inviteCode=${encodeURIComponent(inviteCode)}`;
            }
            // console.log('即将跳转登录页:', OAUTH_URL);
            window.location.href = OAUTH_URL;
        }
        return false;
    }

    // 记录地址
    setUrl = (url) => {
        // 区分web记录地址
        if (this.isSessionStorage()&&window) {
            window.localStorage.setItem(JUMP_URL_LABEL, url);   // 存储到 localStorage，避免 sessionStorage 在新标签页丢失
        }
        return true;
    };

    async fetchUserInfo(params) {
        const loginStateVersion = this.loginStateVersion;
        const res = await readUser(params);
        // 退出前发出的请求不能在返回后重新恢复登录态。
        if (loginStateVersion !== this.loginStateVersion) return res;
        if (res.code === 0) {
            runInAction(() => {
                this.userInfo = res.data;
                this.isLoggedIn = true;
                this.userMeta = res.meta
            });
        } else {
            runInAction(() => {
                this.isLoggedIn = false;
            });
        }
        return res
    }

    // 根据 Cookie 恢复全局登录态并补齐用户信息；并发调用时复用同一个请求。
    async hydrateLoginState() {
        const userCode = getCookie('user_code');
        const token = getCookie('access_token');

        if (token) this.setToken(token);
        if (!userCode) return null;

        runInAction(() => {
            this.isLoggedIn = true;
        });

        if (String(this.userInfo?.code || '') === String(userCode)) return this.userInfo;
        if (this.loginHydrationPromise) return this.loginHydrationPromise;

        this.loginHydrationPromise = this.fetchUserInfo({ code: userCode });
        try {
            return await this.loginHydrationPromise;
        } finally {
            this.loginHydrationPromise = null;
        }
    }

    @action
    async getUserInfo(params) {
        const res = await readUser(params);
        return res
    }

    // 获取当前登录用户的等级和商城折扣权益。
    @action
    async getUserLevel(params = {}) {
        return await readUserLevel(params);
    }

    // 获取 V 币明细
    @action
    async getUserScoreRecords(params = {}) {
        return await readUserScoreRecords(params);
    }
    async fetchUserPermissions(ctx = null) {
        try {
            const res = await readPermissions({}, ctx);
            if (res.code === 0) {
                runInAction(() => {
                    this.permissions = res.data;
                });
            }
            return res;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
            });
            throw error;
        }
    }

    // 获取用户勋章
    async getUserMedal(params) {
        const res = await readUserMedal(params);
        if (res.code === 0) {
            runInAction(() => {
                this.userMedal = res.data;
            });
        }
        return res
    }

    // 关注用户
    @action
    async createFollowUser(params) {
        const res = await createFollow(params);
        return res
    }

    // 取消关注用户或者移除粉丝
    @action
    async followDelete(params) {
        const res = await followDelete(params);
        return res
    }


    /**
    * 获取帖子列表（异步）
    * 统一管理 loading、错误、分页、hasMore 等状态
    * 页面和 list 组件可直接响应 listStore 的状态
    * @param {object} params 查询参数
    *   - params.page[number]: 页码
    *   - params.page[size]: 每页数量
    *   - params.filter: 过滤条件（可选）
    * @returns {Promise<void>}
    */
    async getUsersThreadsList(code, params) {
        // 标记当前请求所属的用户 code，便于忽略过期响应
        this.currentUsersCode = code;
        // 1. 标记 loading 状态，页面可直接响应
        this.listStore.setAttribute({
            namespace: 'usersThreadsList',
            key: 'loading',
            value: true
        });
        // 2. 发起异步请求，获取帖子列表
        const res = await readUsersThreads(code, params);

        // 在 runInAction 中更新状态
        // 如果在请求期间用户已切换，直接忽略返回结果，避免旧数据污染
        if (code !== this.currentUsersCode) {
            return res;
        }

        runInAction(() => {
            if (res.code === 0) {
                // 3. 请求成功，写入列表数据，自动分页累加
                this.listStore.setList({
                    namespace: 'usersThreadsList',
                    data: { data: res.data }, // 兼容 setList 的数据结构
                    page: params['page[number]'], // 当前页码
                    path: 'data.data', // 数据路径，适配后端返回结构
                    search: { filter: params.filter || {} }, // 记录查询条件
                    hasMore: res.data.page ? !!res.data.page.hasMore : false // 是否还有更多
                });
                // 4. 清除错误状态
                this.listStore.setAttribute({
                    namespace: 'usersThreadsList',
                    key: 'requestError',
                    value: { isError: false, errorText: '' }
                });
            } else {
                // 5. 请求失败，写入错误信息，页面可直接响应
                this.listStore.setAttribute({
                    namespace: 'usersThreadsList',
                    key: 'requestError',
                    value: { isError: true, errorText: res.message || '加载失败' }
                });
            }
            // 6. 关闭 loading 状态
            this.listStore.setAttribute({
                namespace: 'usersThreadsList',
                key: 'loading',
                value: false
            });
        });

        return res;
    }


    /**
    * 获取用户评论列表（异步）
    * 统一管理 loading、错误、分页、hasMore 等状态
    * 页面和 list 组件可直接响应 listStore 的状态
    * @param {object} params 查询参数
    *   - params.page[number]: 页码
    *   - params.page[size]: 每页数量
    *   - params.filter: 过滤条件（可选）
    * @returns {Promise<void>}
    */
    async getUsersPostsList(code, params) {
        // 标记当前请求所属的用户 code，便于忽略过期响应
        this.currentUsersCode = code;
        // 1. 标记 loading 状态，页面可直接响应
        this.listStore.setAttribute({
            namespace: 'usersPostsList',
            key: 'loading',
            value: true
        });
        // 2. 发起异步请求，获取帖子列表
        const res = await readUsersPosts(code, params);

        // 在 runInAction 中更新状态
        // 如果在请求期间用户已切换，直接忽略返回结果，避免旧数据污染
        if (code !== this.currentUsersCode) {
            return res;
        }

        runInAction(() => {
            if (res.code === 0) {
                // 3. 请求成功，写入列表数据，自动分页累加
                this.listStore.setList({
                    namespace: 'usersPostsList',
                    data: { data: res.data }, // 兼容 setList 的数据结构
                    page: params['page[number]'], // 当前页码
                    path: 'data.data', // 数据路径，适配后端返回结构
                    search: { filter: params.filter || {} }, // 记录查询条件
                    hasMore: res.data.page ? !!res.data.page.hasMore : false // 是否还有更多
                });
                // 4. 清除错误状态
                this.listStore.setAttribute({
                    namespace: 'usersPostsList',
                    key: 'requestError',
                    value: { isError: false, errorText: '' }
                });
            } else {
                // 5. 请求失败，写入错误信息，页面可直接响应
                this.listStore.setAttribute({
                    namespace: 'usersPostsList',
                    key: 'requestError',
                    value: { isError: true, errorText: res.message || '加载失败' }
                });
            }
            // 6. 关闭 loading 状态
            this.listStore.setAttribute({
                namespace: 'usersPostsList',
                key: 'loading',
                value: false
            });
        });

        return res;
    }

    /**
    * 获取用户点赞列表（异步）
    * 统一管理 loading、错误、分页、hasMore 等状态
    * 页面和 list 组件可直接响应 listStore 的状态
    * @param {object} params 查询参数
    *   - params.page[number]: 页码
    *   - params.page[size]: 每页数量
    *   - params.filter: 过滤条件（可选）
    * @returns {Promise<void>}
    */
    async getUsersLikesList(code, params) {
        // 标记当前请求所属的用户 code，便于忽略过期响应
        this.currentUsersCode = code;
        // 1. 标记 loading 状态，页面可直接响应
        runInAction(() => {
            this.listStore.setAttribute({
                namespace: 'usersLikesList',
                key: 'loading',
                value: true
            });
        });
        // 2. 发起异步请求，获取点赞列表
        const res = await readUsersLikes(code, params);

        // 在 runInAction 中更新状态
        // 如果在请求期间用户已切换，直接忽略返回结果，避免旧数据污染
        if (code !== this.currentUsersCode) {
            return res;
        }

        runInAction(() => {
            if (res.code === 0) {
                // 3. 转换数据结构：点赞 API 返回结构为 {data: [{like: {..., thread: {...}}}, ...], page: {...}}
                // 需要先提取 res.data.data 数组，然后从每个 like 对象中提取 thread
                const dataArray = res.data.data || res.data || [];
                const transformedData = (Array.isArray(dataArray) ? dataArray : []).map(item => item.thread || item);
                // 4. 请求成功，写入列表数据，自动分页累加
                this.listStore.setList({
                    namespace: 'usersLikesList',
                    data: { data: transformedData }, // 提取后的 thread 数据
                    page: params['page[number]'], // 当前页码
                    path: 'data', // 数据路径，transformedData 已经是数组
                    search: { filter: params.filter || {} }, // 记录查询条件
                    hasMore: res.data.page ? !!res.data.page.hasMore : false // 是否还有更多
                });
                // 5. 清除错误状态
                this.listStore.setAttribute({
                    namespace: 'usersLikesList',
                    key: 'requestError',
                    value: { isError: false, errorText: '' }
                });
            } else {
                // 6. 请求失败，写入错误信息，页面可直接响应
                this.listStore.setAttribute({
                    namespace: 'usersLikesList',
                    key: 'requestError',
                    value: { isError: true, errorText: res.message || '加载失败' }
                });
            }
            // 7. 关闭 loading 状态
            this.listStore.setAttribute({
                namespace: 'usersLikesList',
                key: 'loading',
                value: false
            });
        });

        return res;
    }

    /**
    * 获取用户收藏列表（异步）
    * 统一管理 loading、错误、分页、hasMore 等状态
    * 页面和 list 组件可直接响应 listStore 的状态
    * @param {object} params 查询参数
    *   - params.page[number]: 页码
    *   - params.page[size]: 每页数量
    *   - params.filter: 过滤条件（可选）
    * @returns {Promise<void>}
    */
    async getUsersFavoritesList(code, params) {
        // 标记当前请求所属的用户 code，便于忽略过期响应
        this.currentUsersCode = code;
        // 1. 标记 loading 状态，页面可直接响应
        runInAction(() => {
            this.listStore.setAttribute({
                namespace: 'usersFavoritesList',
                key: 'loading',
                value: true
            });
        });
        // 2. 发起异步请求，获取收藏列表
        const res = await readUsersFavorites(code, params);

        // 在 runInAction 中更新状态
        // 如果在请求期间用户已切换，直接忽略返回结果，避免旧数据污染
        if (code !== this.currentUsersCode) {
            return res;
        }

        runInAction(() => {
            if (res.code === 0) {
                // 3. 转换数据结构：收藏 API 返回结构为 {data: [{favorite: {..., thread: {...}}}, ...], page: {...}}
                // 需要先提取 res.data.data 数组，然后从每个 favorite 对象中提取 thread
                const dataArray = res.data.data || res.data || [];
                const transformedData = (Array.isArray(dataArray) ? dataArray : []).map(item => item.thread || item);
                // 4. 请求成功，写入列表数据，自动分页累加
                this.listStore.setList({
                    namespace: 'usersFavoritesList',
                    data: { data: transformedData }, // 提取后的 thread 数据
                    page: params['page[number]'], // 当前页码
                    path: 'data', // 数据路径，transformedData 已经是数组
                    search: { filter: params.filter || {} }, // 记录查询条件
                    hasMore: res.data.page ? !!res.data.page.hasMore : false // 是否还有更多
                });
                // 5. 清除错误状态
                this.listStore.setAttribute({
                    namespace: 'usersFavoritesList',
                    key: 'requestError',
                    value: { isError: false, errorText: '' }
                });
            } else {
                // 6. 请求失败，写入错误信息，页面可直接响应
                this.listStore.setAttribute({
                    namespace: 'usersFavoritesList',
                    key: 'requestError',
                    value: { isError: true, errorText: res.message || '加载失败' }
                });
            }
            // 7. 关闭 loading 状态
            this.listStore.setAttribute({
                namespace: 'usersFavoritesList',
                key: 'loading',
                value: false
            });
        });

        return res;
    }

    // 获取关注/粉丝列表（type: 1 我的关注 / 2 我的粉丝）
    async fetchFollowList(params = {}) {
        this.listStore.setAttribute({ namespace: 'followList', key: 'loading', value: true });
        const res = await readFollowList(params);
        const pageNo = params.page || params['page[number]'] || 1;

        runInAction(() => {
            if (res.code === 0) {
                const hasMore = res.data?.page?.hasMore
                    ?? (typeof res.data?.totalPage === 'number' ? pageNo < res.data.totalPage : false)
                    ?? false;
                // 兼容接口返回的不同数据路径：data.data 或 data.pageData
                const path = res.data && Array.isArray(res.data.pageData) ? 'data.pageData' : 'data.data';
                this.listStore.setList({
                    namespace: 'followList',
                    data: { data: res.data },
                    page: pageNo,
                    path,
                    hasMore,
                    search: params,
                });
                this.listStore.setAttribute({ namespace: 'followList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'followList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'followList', key: 'loading', value: false });
        });

        return res;
    }

    // 获取消息列表 filter[type]：system系统通知, related艾特@我的, replied回复我的, liked点赞通知, favorited 收藏通知
    async fetchNotificationsList(params = {}) {
        this.listStore.setAttribute({ namespace: 'userNotificationsList', key: 'loading', value: true });
        const res = await readNotifications(params);
        const pageNo = params.page || params['page[number]'] || 1;

        runInAction(() => {
            if (res.code === 0) {
                const hasMore = res.data?.page?.hasMore
                    ?? (typeof res.data?.totalPage === 'number' ? pageNo < res.data.totalPage : false)
                    ?? false;
                // 兼容接口返回的不同数据路径：data.data 或 data.pageData
                const path = res.data && Array.isArray(res.data.pageData) ? 'data.pageData' : 'data.data';
                this.listStore.setList({
                    namespace: 'userNotificationsList',
                    data: { data: res.data },
                    page: pageNo,
                    path,
                    hasMore,
                    search: params,
                });
                this.listStore.setAttribute({ namespace: 'userNotificationsList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'userNotificationsList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'userNotificationsList', key: 'loading', value: false });
        });

        return res;
    }

    // 获取私信列表
    async fetchChatList(params = {}) {
        this.listStore.setAttribute({ namespace: 'chatList', key: 'loading', value: true });
        const res = await getChatList(params);
        const pageNo = params.page || params['page[number]'] || 1;

        runInAction(() => {
            if (res.code === 0) {
                const hasMore = res.data?.page?.hasMore
                    ?? (typeof res.data?.totalPage === 'number' ? pageNo < res.data.totalPage : false)
                    ?? false;
                // 兼容接口返回的不同数据路径：data.data 或 data.pageData
                const path = res.data && Array.isArray(res.data.pageData) ? 'data.pageData' : 'data.data';
                this.listStore.setList({
                    namespace: 'chatList',
                    data: { data: res.data },
                    page: pageNo,
                    path,
                    hasMore,
                    search: params,
                });
                this.listStore.setAttribute({ namespace: 'chatList', key: 'requestError', value: { isError: false, errorText: '' } });
                this.setChatTotalCount(res.data?.totalCount)
            } else {
                this.listStore.setAttribute({ namespace: 'chatList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'chatList', key: 'loading', value: false });
        });

        return res;
    }

    // 获取用户的私信消息列表
    async fetchChatMessageList(params = {}) {
        this.listStore.setAttribute({ namespace: 'userChatMessageList', key: 'loading', value: true });
        const res = await getChatMessageList(params);
        const pageNo = params.page || params['page[number]'] || 1;

        runInAction(() => {
            if (res.code === 0) {
                const hasMore = res.data?.page?.hasMore
                    ?? (typeof res.data?.totalPage === 'number' ? pageNo < res.data.totalPage : false)
                    ?? false;
                // 兼容接口返回的不同数据路径：data.data 或 data.pageData
                const path = res.data && Array.isArray(res.data.pageData) ? 'data.pageData' : 'data.data';
                this.listStore.setList({
                    namespace: 'userChatMessageList',
                    data: { data: res.data },
                    page: pageNo,
                    path,
                    hasMore,
                    search: params,
                });
                this.listStore.setAttribute({ namespace: 'userChatMessageList', key: 'requestError', value: { isError: false, errorText: '' } });
                this.setChatTotalCount(res.data?.totalCount)
            } else {
                this.listStore.setAttribute({ namespace: 'userChatMessageList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'userChatMessageList', key: 'loading', value: false });
        });

        return res;
    }

    // 设置私信总数量
    @action
    setChatTotalCount(chatTotalCount) {
        this.chatTotalCount = chatTotalCount;
    }
    
    // 获取未读消息数
    @action
    async getUnreadNotifications(params) {
        const res = await unreadNotifications(params);
        return res
    }
    
    // 获取与某一用户的对话框
    @action
    async getChatRoomFind(params) {
        const res = await getChatRoomFind(params);
        return res
    }
    
    // 消息一键已读
    @action
    async submitNotificationsRead(params) {
        const res = await submitNotificationsRead(params);
        return res
    }

    // 清空私信列表所有会话的未读数
    @action
    clearChatListUnreadCount() {
        const namespace = 'chatList';
        const list = this.listStore.lists[namespace];
        if (!list) return;

        let hasUpdated = false;
        Object.keys(list.data).forEach((page) => {
            list.data[page].forEach((item, index) => {
                if (item.unreadCount > 0) {
                    list.data[page][index] = { ...item, unreadCount: 0 };
                    hasUpdated = true;
                }
            });
        });

        if (hasUpdated) {
            this.listStore.lists = { ...this.listStore.lists };
        }
    }

    // 一键已读：清空所有未读（私信 + 其他通知）
    async markAllNotificationsAsRead() {
        const res = await submitNotificationsRead({ type: 'all' });
        if (res.code === 0) {
            runInAction(() => {
                this.clearChatListUnreadCount();
                if (this.userMeta) {
                    this.userMeta = { ...this.userMeta, unreadCount: 0 };
                }
            });
            if (this.userInfo?.code) {
                await this.fetchUserInfo({ code: this.userInfo.code });
            }
        }
        return res;
    }

    // 发私信
    @action
    async submitChatMessageSendData(params) {
        const res = await submitChatMessageSend(params);
        return res
    }
    
    // 获取推荐用户列表
    async fetchRecommendUserList(params = {}) {
        this.listStore.setAttribute({ namespace: 'recommendUserList', key: 'loading', value: true });
        const res = await readRecommendUserList(params);
        const pageNo = params.page || params['page[number]'] || 1;

        runInAction(() => {
            if (res.code === 0) {
                const hasMore = res.data?.page?.hasMore
                    ?? (typeof res.data?.totalPage === 'number' ? pageNo < res.data.totalPage : false)
                    ?? false;
                // 兼容接口返回的不同数据路径：data.data 或 data.pageData
                const path = res.data && Array.isArray(res.data.pageData) ? 'data.pageData' : 'data.data';
                this.listStore.setList({
                    namespace: 'recommendUserList',
                    data: { data: res.data },
                    page: pageNo,
                    path,
                    hasMore,
                    search: params,
                });
                this.listStore.setAttribute({ namespace: 'recommendUserList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'recommendUserList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'recommendUserList', key: 'loading', value: false });
        });

        return res;
    }

    // 搜索用户列表
    async fetchSearchUsersList(params = {}) {
        this.listStore.setAttribute({ namespace: 'searchUsersList', key: 'loading', value: true });
        const res = await searchUsersList(params);
        const pageNo = params.page || params['page[number]'] || 1;

        runInAction(() => {
            if (res.code === 0) {
                const hasMore = res.data?.page?.hasMore
                    ?? (typeof res.data?.totalPage === 'number' ? pageNo < res.data.totalPage : false)
                    ?? false;
                // 兼容接口返回的不同数据路径：data.data 或 data.pageData
                const path = res.data && Array.isArray(res.data.pageData) ? 'data.pageData' : 'data.data';
                this.listStore.setList({
                    namespace: 'searchUsersList',
                    data: { data: res.data },
                    page: pageNo,
                    path,
                    hasMore,
                    search: params,
                });
                this.listStore.setAttribute({ namespace: 'searchUsersList', key: 'requestError', value: { isError: false, errorText: '' } });
            } else {
                this.listStore.setAttribute({ namespace: 'searchUsersList', key: 'requestError', value: { isError: true, errorText: res.message || '加载失败' } });
            }
            this.listStore.setAttribute({ namespace: 'searchUsersList', key: 'loading', value: false });
        });

        return res;
    }


    /**
     * 直接设置 usersThreadsList listStore 统一管理）
     * @param {array} usersThreadsList 帖子列表
     */
    @action
    setUsersThreadsList(usersThreadsList) {
        this.usersThreadsList = usersThreadsList;
    }


    @action
    setUserInfo(userInfo) {
        this.userInfo = userInfo;
        this.isLoggedIn = !!userInfo;
    }

    @action
    setToken(token) {
        this.token = token;
        this.isLoggedIn = !!token;
    }

    @action
    setPermissions(permissions) {
        this.permissions = permissions;
    }

    @action
    setLoggedIn(loggedIn) {
        this.isLoggedIn = loggedIn;
    }

    @action
    setRememberMe(rememberMe) {
        this.rememberMe = rememberMe;
    }

    @action
    setUserMedal(userMedal) {
        this.userMedal = userMedal;
    }

    @action
    logout({ clearCookies = true } = {}) {
        this.loginStateVersion += 1;
        this.loginHydrationPromise = null;
        this.userInfo = null;
        this.token = null;
        this.permissions = [];
        this.isLoggedIn = false;
        this.rememberMe = false;
        this.error = null;
        this.userMeta = null;

        if (clearCookies) clearAuthCookies();
        // 存储被浏览器禁用时，仍须完成 Cookie 清理和退出。
        try {
            ['token', 'access_token', 'user_code', 'userInfo', JUMP_URL_LABEL].forEach(key => localStorage.removeItem(key));
        } catch (e) {}
        try { sessionStorage.clear(); } catch (e) {}
    }

    // 清理本地状态并导航到服务端；XHR 无法完成跨域退出重定向。
    async logoutRemote() {
        const redirectUrl = typeof window !== 'undefined' ? this.getCurrentUrl() : '';
        try {
            // 同时注销开发代理携带的本地会话；接口跳转仍交由下面的页面导航完成。
            await userLogout({ redirect_uri: redirectUrl });
        } finally {
            // 服务端还需要收到原 Cookie 才能注销会话，不能在导航前将其删除。
            this.logout({ clearCookies: false });
            if (typeof window !== 'undefined') {
                window.location.href = getLogoutUrl(redirectUrl);
            }
        }
    }

    @action
    onEnsureLogin = (callback) => {
        if (!this.checkLogin()) {
            // 未登录，checkLogin 会跳转到登录页
            return;
        }
        // 已登录，执行传入的回调函数
        if (typeof callback === 'function') {
            callback();
        }
    };

    /**
     * 清空所有 user store 的数据
     * 用于在进入 user 页面时清空旧的用户数据，避免数据污染
     * 注意：不清空登录状态，避免跳转后需要刷新才能显示登录状态
     */
    @action
    clearAllUserData() {
        // 清空用户信息，但保留登录状态
        this.userInfo = null;
        this.userMedal = null;
        this.currentUsersCode = '';

        // 清空列表数据
        if (this.listStore && this.listStore.lists) {
            // 使用 clearList，确保所有分页数据与状态彻底清空
            this.listStore.clearList({ namespace: 'usersThreadsList' });
            this.listStore.clearList({ namespace: 'followList' });
            this.listStore.clearList({ namespace: 'recommendUserList' });
        }

        // 清空加载状态
        this.isLoading = false;
        this.error = null;

        // 注意：不重置 isLoggedIn 状态，避免登录状态丢失
    }

    /**
     * 清空用户页面相关数据（不包括登录状态）
     * 用于在进入其他用户页面时清空旧的用户数据，避免数据污染
     */
    @action
    clearUserPageData() {
        // 只清空用户页面相关数据，不清空登录状态和当前用户信息
        this.userMedal = null;
        this.currentUsersCode = '';

        // 清空列表数据
        if (this.listStore && this.listStore.lists) {
            // 使用 clearList，确保所有分页数据与状态彻底清空
            this.listStore.clearList({ namespace: 'usersThreadsList' });
            this.listStore.clearList({ namespace: 'followList' });
            this.listStore.clearList({ namespace: 'recommendUserList' });
        }

        // 清空加载状态
        this.isLoading = false;
        this.error = null;
    }
}

const userAction = new UserAction();
export default userAction;
